import React, { useEffect, useMemo, useState, useCallback, useLayoutEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { apiUrl } from '../config/api';
import computeMetrics from '../utils/cardMetrics';

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function monthName(m) {
  const names = ['янв','фев','мар','апр','май','июн','июл','авг','сен','окт','ноя','дек'];
  const i = Math.max(1, Math.min(12, Number(m||0))) - 1;
  return names[i] || String(m);
}

function shiftPeriod(year, month, deltaMonths) {
  const baseIndex = Number(year) * 12 + (Number(month) - 1) + deltaMonths;
  const nextYear = Math.floor(baseIndex / 12);
  const nextMonth = ((baseIndex % 12) + 12) % 12 + 1;
  return { year: nextYear, month: nextMonth };
}

const fmtNum = (v) => (v == null ? '—' : Number(v).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

const calcClosingStatsFromRows = (rows = []) => {
  const totals = { invoiced: 0, debt: 0, unsigned: 0 };
  const byCard = new Map();
  rows.forEach((row) => {
    const amount = Number(row.amountCustomer ?? row.amount_customer ?? 0) || 0;
    const paid = Number(row.paidAmount ?? row.paid_amount ?? 0) || 0;
    const signed = Number(row.signedAmount ?? row.signed_amount ?? 0) || 0;
    const debt = Math.max(0, amount - paid);
    const unsigned = Math.max(0, amount - signed);
    totals.invoiced += amount;
    totals.debt += debt;
    totals.unsigned += unsigned;
    const cardIdRaw = row.cardId ?? row.card_id ?? row.cardID ?? row.cardid;
    const cardId = Number(cardIdRaw);
    if (Number.isFinite(cardId) && cardId > 0) {
      const bucket = byCard.get(cardId) || { invoiced: 0, debt: 0, unsigned: 0 };
      bucket.invoiced += amount;
      bucket.debt += debt;
      bucket.unsigned += unsigned;
      byCard.set(cardId, bucket);
    }
  });
  return { totals, byCard };
};

const emptyClosingStats = () => ({ totals: { invoiced: 0, debt: 0, unsigned: 0 }, byCard: new Map() });

const debtBadgeStyle = (value) => {
  const isZero = Math.abs(Number(value) || 0) < 0.0001;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: '2px 8px',
    borderRadius: 999,
    fontWeight: 700,
    background: isZero ? '#dcfce7' : '#fee2e2',
    color: isZero ? '#166534' : '#991b1b',
    minWidth: 86,
    textAlign: 'right',
  };
};
const renderDebtBadge = (value) => <span style={debtBadgeStyle(value)}>{fmtNum(value)}</span>;

const profitStyle = (value, { isPercent = false } = {}) => {
  const v = Number(value) || 0; const abs = Math.abs(v);
  const max = isPercent ? 30 : 1_000_000; const t = Math.max(0, Math.min(1, abs / max));
  const fill = Math.round(t * 100); const alpha = 0.12 + t * 0.38;
  const pos = v > 0; const base = pos ? `rgba(34,197,94,${alpha})` : `rgba(239,68,68,${alpha})`;
  const text = pos ? '#065f46' : '#7f1d1d'; const border = pos ? '1px solid rgba(16,185,129,0.35)' : '1px solid rgba(239,68,68,0.35)';
  return { background: `linear-gradient(90deg, ${base} ${fill}%, rgba(0,0,0,0) ${fill}%)`, color: text, border, borderRadius: 6, padding: '2px 6px', display: 'inline-block', minWidth: 86, textAlign: 'right' };
};

export default function SupercontrolPivot() {
  const API_BASE = apiUrl();
  const q = useQuery();
  const year = Number(q.get('year')) || 0;
  const month = Number(q.get('month')) || 0;
  const prevYearPeriod = shiftPeriod(year, month, -12);
  const prevMonthPeriod = shiftPeriod(year, month, -1);
  const nextMonthPeriod = shiftPeriod(year, month, 1);
  const nextYearPeriod = shiftPeriod(year, month, 12);

  // guard: only superusers (страница уже защищена роутом, но на всякий случай)
  let isSuper = false;
  try { const u = JSON.parse(localStorage.getItem('authUser') || 'null'); isSuper = ['1001','1002'].includes(String(u?.id)); } catch {}

  const periodNavLinkStyle = {
    width: 34,
    height: 34,
    borderRadius: 10,
    border: '1px solid #cbd5e1',
    background: '#fff',
    color: '#0f172a',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    textDecoration: 'none',
    fontSize: 18,
    lineHeight: 1,
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cardsById, setCardsById] = useState(new Map());
  const [cardIds, setCardIds] = useState([]);
  const [closingStats, setClosingStats] = useState(() => emptyClosingStats());
  const tableRef = useRef(null);
  const scrollRef = useRef(null);
  const topScrollRef = useRef(null);
  const [scrollWidth, setScrollWidth] = useState(0);

  useEffect(() => { document.title = `Суперконтроль — разрез ${String(month).padStart(2,'0')}.${year}`; }, [month, year]);

  const load = useCallback(async () => {
    if (!year || !month) return;
    try {
      setLoading(true); setError('');
      const qs = new URLSearchParams({ fromMonth: month, fromYear: year, toMonth: month, toYear: year }).toString();
      const closingPromise = fetch(`${API_BASE}/api/closing-docs?month=${month}&year=${year}`)
        .then((resp) => { if (!resp.ok) throw new Error('failed'); return resp.json(); })
        .catch(() => ({ rows: [] }));
      const [sumRes, listRes, closingRes] = await Promise.all([
        fetch(`${API_BASE}/api/supercontrol/summary?${qs}`).then(r=>r.json()),
        fetch(`${API_BASE}/api/card-calc?full=1&perPage=2000&withDelivered=1`).then(r=>r.json()),
        closingPromise,
      ]);
      const items = Array.isArray(listRes.items) ? listRes.items : (Array.isArray(listRes) ? listRes : []);
      const map = new Map(items.map(c => [c.id, c]));
      setCardsById(map);
      const m0 = (sumRes?.months || []).find(x => x.year === year && x.month === month) || { cardIds: [] };
      setCardIds(Array.isArray(m0.cardIds) ? m0.cardIds : []);
      const rows = Array.isArray(closingRes?.rows) ? closingRes.rows : [];
      setClosingStats(calcClosingStatsFromRows(rows));
    } catch (e) {
      setError('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  }, [year, month, API_BASE]);

  useEffect(() => { load(); }, [load]);

  const rowsDef = useMemo(() => ([
    { key: 'contractTotal', title: 'Сумма контракта, руб.' },
    { key: 'profit_on_hand', title: 'Прибыль на руки, руб.' },
    { key: 'profit_on_hand_pct', title: 'Рентабельность чистая, %', isPercent: true },
    { key: 'closing_invoiced', title: 'Выставлено счетов (реестр)' },
    { key: 'closing_debt', title: 'Дебиторка по реестру', highlightDebt: true },
    { key: 'closing_unsigned', title: 'Неподписанные акты', highlightDebt: true },
    { key: 'fot_total', title: 'Общая сумма ФОТ (карта+нал)' },
    { key: 'fot_cash', title: 'Сумма ФОТ нал', triplet: ['fot_cash_sum', 'fot_cash_paid', 'fot_cash_remain'] },
    { key: 'fot_card', title: 'Сумма ФОТ на карту', triplet: ['fot_card_sum', 'fot_card_paid', 'fot_card_remain'] },
    { key: 'ndfl', title: 'НДФЛ, руб.' },
    { key: 'insurance', title: 'Страховые взносы' },
    { key: 'fot_tax', title: 'Сумма налогов на ФОТ, руб.' },
    { key: 'tmc_with_vat', title: 'ТМЦ (с НДС)', triplet: ['tmc_with_vat', 'tmc_paid_manual', 'tmc_remain'] },
    { key: 'amort_month', title: 'Амортизация оборудования и инвентаря' },
    { key: 'extras_vat', title: 'Доп.затраты, с НДС', triplet: ['extras_vat', 'extras_vat_paid', 'extras_vat_remain'] },
    { key: 'extras_no_vat', title: 'Доп.затраты, без НДС', triplet: ['extras_no_vat', 'extras_no_vat_paid', 'extras_no_vat_remain'] },
    { key: 'extras_cash', title: 'Доп.затраты, наличные', triplet: ['extras_cash', 'extras_cash_paid', 'extras_cash_remain'] },
    { key: 'vat_to_pay', title: 'НДС начисленный' },
    { key: 'vat_deduction', title: 'НДС в зачет' },
    { key: 'vat_delta', title: 'НДС на оплату' },
    { key: 'overhead', title: 'Накладные расходы' },
    { key: 'profit_tax', title: 'Налог на прибыль' },
    { key: 'cost_price', title: 'Себестоимость контракта' },
    { key: 'net_profit', title: 'Прибыль контракта' },
    { key: 'profitability_pct', title: 'Рентабельность, %', isPercent: true },
    { key: 'break_even', title: 'Точка безубыточности (справочно)' },
    { key: 'cash_remain_total', title: 'Итого осталось выплатить нал' },
    { key: 'subcontract_base', title: 'Субподряд На мыло (с НДС)' },
    { key: 'soap_profit_on_hand', title: 'Получено нал всего' },
    { key: 'vat_base_total', title: 'Чистая прибыль белая' },
    { key: 'base_total', title: 'ИТОГО затраты с налогами (без накладных)' },
    { key: 'soap_share_frac', title: 'Процент выручки минус затраты → мыло' },
  ]), []);

  const cols = useMemo(() => cardIds.map(id => cardsById.get(id)).filter(Boolean), [cardIds, cardsById]);
  const metricsById = useMemo(() => {
    const closingByCard = closingStats?.byCard instanceof Map ? closingStats.byCard : new Map();
    return new Map(cols.map((c) => {
      const metrics = computeMetrics(c);
      const closing = closingByCard.get(c.id) || { invoiced: 0, debt: 0, unsigned: 0 };
      metrics.closing_invoiced = closing.invoiced;
      metrics.closing_debt = closing.debt;
      metrics.closing_unsigned = closing.unsigned;
      return [c.id, metrics];
    }));
  }, [cols, closingStats]);

  // Totals по выбранным карточкам
  const totalsP = useMemo(() => {
    const t = {};
    const metricsList = cols.map(c => metricsById.get(c.id)).filter(Boolean);
    const sum = (key) => metricsList.reduce((s, m) => s + (Number(m?.[key]) || 0), 0);
    const weighted = (key) => {
      let wsum = 0, w = 0; for (const m of metricsList) { const v = Number(m?.[key]) || 0; const wt = Number(m?.contractTotal)||0; wsum += v*wt; w += wt; } return w>0? (wsum/w):0;
    };
    for (const r of rowsDef) {
      if (r.triplet) { const [a,b,c] = r.triplet; t[r.key] = { a: sum(a), b: sum(b), c: sum(c) }; }
      else if (r.isPercent) { t[r.key] = weighted(r.key); }
      else { t[r.key] = sum(r.key); }
    }
    return t;
  }, [cols, metricsById, rowsDef]);

  const tableMinWidth = useMemo(() => Math.max(900, 260 + 180 + cols.length * 180), [cols.length]);
  const gridColor = '#e5e7eb';
  const headerBg = '#f5f5f5';
  const totalColBg = '#fffdf4';
  const mainTableBaseStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    border: `1px solid ${gridColor}`,
    boxShadow: '0 22px 44px rgba(15,23,42,0.08)',
  };
  const stickyCornerHeadStyle = {
    padding: 10,
    textAlign: 'left',
    border: `1px solid ${gridColor}`,
    position: 'sticky',
    top: 0,
    left: 0,
    zIndex: 6,
    background: headerBg,
    boxShadow: '0 2px 0 rgba(0,0,0,0.04)',
  };
  const stickyTotalHeadStyle = {
    padding: 10,
    textAlign: 'right',
    border: `1px solid ${gridColor}`,
    position: 'sticky',
    top: 0,
    zIndex: 5,
    background: '#fff8e5',
    fontWeight: 800,
    boxShadow: '0 2px 0 rgba(0,0,0,0.04)',
  };
  const monthHeadStyle = {
    padding: 10,
    textAlign: 'center',
    border: `1px solid ${gridColor}`,
    position: 'sticky',
    top: 0,
    background: headerBg,
    boxShadow: '0 2px 0 rgba(0,0,0,0.04)',
  };
  const stickyRowLabelStyle = {
    padding: 10,
    textAlign: 'left',
    fontWeight: 700,
    border: `1px solid ${gridColor}`,
    position: 'sticky',
    left: 0,
    zIndex: 3,
    boxShadow: '2px 0 0 rgba(0,0,0,0.04)',
  };
  const totalCellStyle = {
    padding: 10,
    textAlign: 'right',
    verticalAlign: 'top',
    border: `1px solid ${gridColor}`,
    background: totalColBg,
  };
  const valueCellStyle = {
    padding: 10,
    textAlign: 'right',
    verticalAlign: 'top',
    border: `1px solid ${gridColor}`,
  };

  const syncScrollLeft = useCallback((source, target) => {
    if (!source || !target) return;
    const next = source.scrollLeft;
    if (Math.abs(target.scrollLeft - next) > 1) {
      target.scrollLeft = next;
    }
  }, []);

  const handleBodyScroll = useCallback(() => {
    syncScrollLeft(scrollRef.current, topScrollRef.current);
  }, [syncScrollLeft]);

  const handleTopScroll = useCallback(() => {
    syncScrollLeft(topScrollRef.current, scrollRef.current);
  }, [syncScrollLeft]);

  useLayoutEffect(() => {
    const updateWidth = () => {
      const width = tableRef.current?.scrollWidth || 0;
      setScrollWidth(width);
      if (topScrollRef.current && scrollRef.current) {
        topScrollRef.current.scrollLeft = scrollRef.current.scrollLeft;
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [cols.length, tableMinWidth]);

  if (!isSuper) return <div style={{ padding: 24, color: '#c00' }}>Доступно только суперпользователям.</div>;
  if (!year || !month) return <div style={{ padding: 24 }}>Не указан месяц. Откройте из страницы Суперконтроля.</div>;

  return (
    <div style={{ padding: '12px 0 0 0', marginTop: -18 }}>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12 }}>
        <Link to="/supercontrol">&larr; Назад к суперконтролю</Link>
        <h2 style={{ margin: 0 }}>Разрез по карточкам: {monthName(month)} {year}</h2>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Link
            to={`/supercontrol/pivot?year=${encodeURIComponent(prevYearPeriod.year)}&month=${encodeURIComponent(prevYearPeriod.month)}`}
            style={periodNavLinkStyle}
            title="Предыдущий год"
            aria-label="Предыдущий год"
          >
            &laquo;
          </Link>
          <Link
            to={`/supercontrol/pivot?year=${encodeURIComponent(prevMonthPeriod.year)}&month=${encodeURIComponent(prevMonthPeriod.month)}`}
            style={periodNavLinkStyle}
            title="Предыдущий месяц"
            aria-label="Предыдущий месяц"
          >
            &lsaquo;
          </Link>
          <Link
            to={`/supercontrol/pivot?year=${encodeURIComponent(nextMonthPeriod.year)}&month=${encodeURIComponent(nextMonthPeriod.month)}`}
            style={periodNavLinkStyle}
            title="Следующий месяц"
            aria-label="Следующий месяц"
          >
            &rsaquo;
          </Link>
          <Link
            to={`/supercontrol/pivot?year=${encodeURIComponent(nextYearPeriod.year)}&month=${encodeURIComponent(nextYearPeriod.month)}`}
            style={periodNavLinkStyle}
            title="Следующий год"
            aria-label="Следующий год"
          >
            &raquo;
          </Link>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center' }}>Загрузка…</div>
      ) : error ? (
        <div style={{ textAlign: 'center', color: '#c00' }}>{error}</div>
      ) : (
        <>
          <div
            style={{
              overflowX: 'auto',
              overflowY: 'hidden',
              height: 14,
              margin: '6px 0',
              borderRadius: 40,
              background: '#e2e8f0',
              scrollbarGutter: 'stable'
            }}
            ref={topScrollRef}
            onScroll={handleTopScroll}
          >
            <div style={{ width: Math.max(scrollWidth, tableMinWidth), height: 8 }} />
          </div>
          <div
            style={{
              overflow: 'auto',
              maxHeight: 'calc(100vh - 180px)',
              borderRadius: 18,
              background: '#fff',
              scrollbarGutter: 'stable'
            }}
            ref={scrollRef}
            onScroll={handleBodyScroll}
          >
            <table ref={tableRef} style={{ ...mainTableBaseStyle, minWidth: tableMinWidth }}>
              <colgroup>
                <col style={{ width: 260 }} />
                <col style={{ width: 170 }} />
                {cols.map((_, i) => (<col key={i} style={{ width: 180 }} />))}
              </colgroup>
              <thead>
                <tr>
                  <th style={stickyCornerHeadStyle}></th>
                  <th style={stickyTotalHeadStyle}>ИТОГО</th>
                  {cols.map((c, i) => (
                    <th key={c.id || i} style={{ ...monthHeadStyle, zIndex: 4, whiteSpace: 'normal' }}>
                      <div style={{ fontWeight: 800 }}>{c.objectName}</div>
                      <div style={{ fontSize: 12 }}>
                        <a href={`/card-calc/${encodeURIComponent(c.objectCodeFull || c.objectCode)}`} target="_blank" rel="noreferrer">
                          {c.objectCodeFull || String(c.objectCode).padStart(4, '0')}
                        </a>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rowsDef.map((r, rowIdx) => {
                  const rowBg = rowIdx % 2 === 0 ? '#ffffff' : '#f8f9fb';
                  return (
                    <tr key={r.key}>
                      <td style={{ ...stickyRowLabelStyle, background: rowBg }}>{r.title}</td>
                      <td style={totalCellStyle}>
                        {r.triplet ? (
                          (() => {
                            const v = totalsP[r.key] || { a: 0, b: 0, c: 0 };
                            const bg = (n) => {
                              const x = Number(n) || 0;
                              const eps = 0.005;
                              if (x > eps) return '#ffe9d9';
                              if (x < -eps) return '#fff3cf';
                              return '#e8f7e5';
                            };
                            return (
                              <div style={{ display: 'grid', gridTemplateRows: '1fr 1fr', gap: 2 }}>
                                <div style={{ fontSize: 13.5, color: '#111827', fontWeight: 800 }}>∑ {fmtNum(v.a)}</div>
                                <div style={{ fontSize: 12, background: bg(v.c), padding: '1px 4px', borderRadius: 4, justifySelf: 'end', fontWeight: 700 }}>ост. {fmtNum(v.c)}</div>
                              </div>
                            );
                          })()
                        ) : r.key === 'profit_on_hand' ? (
                          <span style={{ ...profitStyle(totalsP[r.key], { isPercent: false }), fontWeight: 700 }}>{fmtNum(totalsP[r.key])}</span>
                        ) : r.key === 'profit_on_hand_pct' ? (
                          <span style={{ ...profitStyle(totalsP[r.key], { isPercent: true }), fontWeight: 700 }}>{fmtNum(totalsP[r.key])}%</span>
                        ) : r.highlightDebt ? (
                          renderDebtBadge(totalsP[r.key])
                        ) : (
                          <span style={{ fontWeight: 700 }}>{fmtNum(totalsP[r.key])}</span>
                        )}
                      </td>
                      {cols.map((c, i) => {
                        const mmc = metricsById.get(c.id) || {};
                        return (
                          <td key={c.id || i} style={{ ...valueCellStyle, background: rowBg }}>
                            {r.triplet ? (
                              (() => {
                                const [a, , c3] = r.triplet;
                                const bg = (n) => {
                                  const x = Number(n) || 0;
                                  const eps = 0.005;
                                  if (x > eps) return '#ffe9d9';
                                  if (x < -eps) return '#fff3cf';
                                  return '#e8f7e5';
                                };
                                return (
                                  <div style={{ display: 'grid', gridTemplateRows: '1fr 1fr', gap: 2 }}>
                                    <div style={{ fontSize: 13.5, color: '#111827', fontWeight: 700 }}>∑ {fmtNum(mmc[a])}</div>
                                    <div style={{ fontSize: 12, background: bg(mmc[c3]), padding: '1px 4px', borderRadius: 4, justifySelf: 'end' }}>ост. {fmtNum(mmc[c3])}</div>
                                  </div>
                                );
                              })()
                            ) : r.key === 'profit_on_hand' ? (
                              <span style={profitStyle(mmc[r.key], { isPercent: false })}>{fmtNum(mmc[r.key])}</span>
                            ) : r.key === 'profit_on_hand_pct' ? (
                              <span style={profitStyle(mmc[r.key], { isPercent: true })}>{fmtNum(mmc[r.key])}%</span>
                            ) : r.highlightDebt ? (
                              renderDebtBadge(mmc[r.key])
                            ) : (
                              fmtNum(mmc[r.key])
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
